import type { ImageQualityResult } from '@/@types/latestImagesV2';

/**
 * Maps FMX slot indices (0-17) to the teeth numbers expected in that slot.
 * Standard FMX layout for adult dentition.
 */
export const FMX_SLOT_TO_TEETH: Record<number, Set<string>> = {
  0: new Set(['1', '2', '3']),
  1: new Set(['3', '4', '5']),
  2: new Set(['5', '6', '7']),
  3: new Set(['8', '9']),
  4: new Set(['10', '11', '12']),
  5: new Set(['12', '13', '14']),
  6: new Set(['14', '15', '16']),
  7: new Set(['1', '2', '3', '30', '31', '32']),
  8: new Set(['4', '5', '28', '29']),
  9: new Set(['12', '13', '20', '21']),
  10: new Set(['14', '15', '16', '17', '18', '19']),
  11: new Set(['30', '31', '32']),
  12: new Set(['28', '29', '30']),
  13: new Set(['26', '27', '28']),
  14: new Set(['24', '25']),
  15: new Set(['21', '22', '23']),
  16: new Set(['19', '20', '21']),
  17: new Set(['17', '18', '19']),
};

/**
 * Filters an array of tooth numbers to only include teeth expected in the given FMX slot.
 * If fmxSlot is null/undefined, returns all teeth (no filtering).
 *
 * @param teeth - Array of tooth numbers (as strings)
 * @param fmxSlot - The FMX slot index (0-17), or null if not in FMX view
 * @returns Array of tooth numbers that are expected in the slot
 */
function filterTeethByFmxSlot(
  teeth: string[],
  fmxSlot: number | null | undefined,
): string[] {
  if (fmxSlot == null || !FMX_SLOT_TO_TEETH[fmxSlot]) {
    return teeth; // No filtering if slot is unknown
  }
  const expectedTeeth = FMX_SLOT_TO_TEETH[fmxSlot];
  return teeth.filter((tooth) => expectedTeeth.has(tooth));
}

export type ImageFindingType =
  | 'missing_root_tips'
  | 'missing_image'
  | 'missing_tooth'
  | 'bitewing_overlap'
  | 'cut_off_edges'
  | 'cej_not_visible';

/**
 * Priority order for findings (lower number = higher priority)
 * Used to deterministically sort findings so the most important shows first
 */
const FINDING_PRIORITY: Record<ImageFindingType, number> = {
  missing_tooth: 1,
  missing_root_tips: 2,
  cut_off_edges: 3,
  cej_not_visible: 4,
  bitewing_overlap: 5,
  missing_image: 6,
};

export interface ImageFinding {
  type: ImageFindingType;
  label: string;
  severity: 'warning' | 'error';
  affectedTeeth?: string[];
}

/**
 * Derives image findings from image quality data for a specific image
 * Findings are sorted by priority (most important first)
 *
 * @param imageId - The image ID to get findings for
 * @param imageQuality - Array of image quality results
 * @param fmxSlot - Optional FMX slot index (0-17). If provided, only findings for teeth
 *                  expected in that slot will be shown.
 */
export function getImageFindingsFromImageQuality(
  imageId: number,
  imageQuality?: ImageQualityResult[],
  fmxSlot?: number | null,
): ImageFinding[] {
  if (!imageQuality) {
    return [];
  }

  const imageData = imageQuality.find((img) => img.image_id === imageId);
  if (!imageData) {
    return [];
  }

  const findings: ImageFinding[] = [];

  // Check for bitewing overlap issues
  if (imageData.is_flagged) {
    findings.push({
      type: 'bitewing_overlap',
      label: 'Overlap detected',
      severity: 'warning',
    });
  }

  // Check for missing root tips
  const missingRootTipsTeeth = filterTeethByFmxSlot(
    imageData.teeth
      .filter((tooth) => tooth.root_visible === false)
      .map((tooth) => tooth.tooth_num)
      .sort(),
    fmxSlot,
  );
  if (missingRootTipsTeeth.length > 0) {
    findings.push({
      type: 'missing_root_tips',
      label: 'Root tips not visible',
      severity: 'warning',
      affectedTeeth: missingRootTipsTeeth,
    });
  }

  // Check for cut off edges
  const cutOffEdgesTeeth = filterTeethByFmxSlot(
    imageData.teeth
      .filter((tooth) => tooth.cut_off_edges && tooth.cut_off_edges.length > 0)
      .map((tooth) => tooth.tooth_num)
      .sort(),
    fmxSlot,
  );

  if (cutOffEdgesTeeth.length > 0) {
    findings.push({
      type: 'cut_off_edges',
      label: 'Edges cut off',
      severity: 'warning',
      affectedTeeth: cutOffEdgesTeeth,
    });
  }

  // Check for missing teeth
  const missingTeeth = filterTeethByFmxSlot(
    imageData.teeth
      .filter((tooth) => tooth.is_missing)
      .map((tooth) => tooth.tooth_num)
      .sort(),
    fmxSlot,
  );
  if (missingTeeth.length > 0) {
    findings.push({
      type: 'missing_tooth',
      label: 'Tooth missing',
      severity: 'warning',
      affectedTeeth: missingTeeth,
    });
  }

  // Check for CEJ visibility issues
  const cejIssuesTeeth = filterTeethByFmxSlot(
    imageData.teeth
      .filter(
        (tooth) =>
          tooth.cej_mesial_visible === false ||
          tooth.cej_distal_visible === false,
      )
      .map((tooth) => tooth.tooth_num)
      .sort(),
    fmxSlot,
  );
  if (cejIssuesTeeth.length > 0) {
    findings.push({
      type: 'cej_not_visible',
      label: 'CEJ not visible',
      severity: 'warning',
      affectedTeeth: cejIssuesTeeth,
    });
  }

  // Sort findings by priority (lower number = higher priority)
  return findings.sort(
    (a, b) => FINDING_PRIORITY[a.type] - FINDING_PRIORITY[b.type],
  );
}

/**
 * Creates a map of image ID to findings for efficient lookup
 */
export function createImageFindingsMap(
  imageQuality?: ImageQualityResult[],
): Map<number, ImageFinding[]> {
  const findingsMap = new Map<number, ImageFinding[]>();

  if (!imageQuality) {
    return findingsMap;
  }

  for (const imageData of imageQuality) {
    const findings = getImageFindingsFromImageQuality(
      imageData.image_id,
      imageQuality,
    );
    if (findings.length > 0) {
      findingsMap.set(imageData.image_id, findings);
    }
  }

  return findingsMap;
}

/** Finding kind used by the UI to render translated title/description with t`...` */
export type ImageQualityFindingKind =
  | 'overlap_left_bitewings'
  | 'overlap_right_bitewings'
  | 'root_tips_not_visible'
  | 'edges_cut_off'
  | 'additional_imaging_required'
  | 'cej_not_visible';

export interface ImageQualityFinding {
  id: string;
  /** Kind used for translation in the UI */
  findingKind: ImageQualityFindingKind;
  title: string;
  description: string;
  /** Teeth list for messages that include tooth numbers (e.g. "for #14, 15") */
  teethList?: string;
  imageId?: number;
  toothNum?: string;
  severity: 'warning' | 'error';
  completed: boolean;
}

/**
 * Generates image quality findings from image quality data
 * Findings are observational (what was detected), not task-oriented
 *
 * @param imageQuality - Array of image quality results
 * @param imageTypeMap - Map of image IDs to image types
 * @param filterImageId - Optional image ID to filter findings for a specific image
 * @param fmxSlot - Optional FMX slot index (0-17). If provided, only findings for teeth
 *                  expected in that slot will be shown.
 */
export function getImageQualityFindingsFromImageQuality(
  imageQuality?: ImageQualityResult[],
  imageTypeMap?: Map<number, string>,
  filterImageId?: number,
  fmxSlot?: number | null,
): ImageQualityFinding[] {
  if (!imageQuality) {
    return [];
  }

  // Filter to specific image if filterImageId is provided
  const filteredImageQuality = filterImageId
    ? imageQuality.filter((img) => img.image_id === filterImageId)
    : imageQuality;

  if (filteredImageQuality.length === 0) {
    return [];
  }

  const findings: ImageQualityFinding[] = [];
  let findingId = 0;

  // Group images by quadrant for better finding organization
  const leftBitewings: ImageQualityResult[] = [];
  const rightBitewings: ImageQualityResult[] = [];
  const leftPeriapicals: ImageQualityResult[] = [];
  const rightPeriapicals: ImageQualityResult[] = [];

  for (const img of filteredImageQuality) {
    const imageType = imageTypeMap?.get(img.image_id) ?? img.image_type;
    const isLeft = img.teeth.some((tooth) => {
      const toothNum = parseInt(tooth.tooth_num, 10);
      // Left side: teeth 17-24 (upper left) and 25-32 (lower left)
      return (
        (toothNum >= 17 && toothNum <= 24) || (toothNum >= 25 && toothNum <= 32)
      );
    });

    if (imageType === 'bitewing' || imageType === 'BITEWING') {
      if (isLeft) {
        leftBitewings.push(img);
      } else {
        rightBitewings.push(img);
      }
    } else if (imageType === 'periapical' || imageType === 'PERIAPICAL') {
      if (isLeft) {
        leftPeriapicals.push(img);
      } else {
        rightPeriapicals.push(img);
      }
    }
  }

  // Check for flagged left bitewings (overlap issues)
  const flaggedLeftBitewings = leftBitewings.filter((img) => img.is_flagged);
  if (flaggedLeftBitewings.length > 0) {
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'overlap_left_bitewings',
      title: 'Interproximal overlap (left bitewings)',
      description: 'Overlap detected on bitewing image(s).',
      severity: 'warning',
      completed: false,
    });
  }

  // Check for flagged right bitewings (overlap issues)
  const flaggedRightBitewings = rightBitewings.filter((img) => img.is_flagged);
  if (flaggedRightBitewings.length > 0) {
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'overlap_right_bitewings',
      title: 'Interproximal overlap (right bitewings)',
      description: 'Overlap detected on bitewing image(s).',
      severity: 'warning',
      completed: false,
    });
  }

  // Check for missing root tips in periapicals
  const missingRootTipsTeeth = new Set<string>();
  for (const img of filteredImageQuality) {
    for (const tooth of img.teeth) {
      if (tooth.root_visible === false) {
        missingRootTipsTeeth.add(tooth.tooth_num);
      }
    }
  }

  const filteredMissingRootTips = filterTeethByFmxSlot(
    Array.from(missingRootTipsTeeth),
    fmxSlot,
  );

  if (filteredMissingRootTips.length > 0) {
    const teethList = filteredMissingRootTips.sort().join(', ');
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'root_tips_not_visible',
      title: 'Root tips not visible',
      description: `Root tips not visible for #${teethList}.`,
      teethList,
      severity: 'warning',
      completed: false,
    });
  }

  // Check for cut off edges
  const cutOffTeeth = new Set<string>();
  for (const img of filteredImageQuality) {
    for (const tooth of img.teeth) {
      if (tooth.cut_off_edges && tooth.cut_off_edges.length > 0) {
        cutOffTeeth.add(tooth.tooth_num);
      }
    }
  }

  const filteredCutOffTeeth = filterTeethByFmxSlot(
    Array.from(cutOffTeeth),
    fmxSlot,
  );

  if (filteredCutOffTeeth.length > 0) {
    const teethList = filteredCutOffTeeth.sort().join(', ');
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'edges_cut_off',
      title: 'Teeth cut off at edges',
      description: `Teeth cut off at edges for #${teethList}.`,
      teethList,
      severity: 'warning',
      completed: false,
    });
  }

  // Check for missing teeth that might need intraorals
  const missingTeethInImages = new Set<string>();
  for (const img of filteredImageQuality) {
    for (const tooth of img.teeth) {
      if (tooth.is_missing) {
        missingTeethInImages.add(tooth.tooth_num);
      }
    }
  }

  const filteredMissingTeeth = filterTeethByFmxSlot(
    Array.from(missingTeethInImages),
    fmxSlot,
  );

  if (filteredMissingTeeth.length > 0) {
    const teethList = filteredMissingTeeth.sort().join(', ');
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'additional_imaging_required',
      title: 'Additional imaging may be required',
      description: `Missing or unclear for #${teethList}; additional imaging may be required.`,
      teethList,
      severity: 'warning',
      completed: false,
    });
  }

  // Check for CEJ visibility issues
  const cejNotVisibleTeeth = new Set<string>();
  for (const img of filteredImageQuality) {
    for (const tooth of img.teeth) {
      if (
        tooth.cej_mesial_visible === false ||
        tooth.cej_distal_visible === false
      ) {
        cejNotVisibleTeeth.add(tooth.tooth_num);
      }
    }
  }

  const filteredCejTeeth = filterTeethByFmxSlot(
    Array.from(cejNotVisibleTeeth),
    fmxSlot,
  );

  if (filteredCejTeeth.length > 0) {
    const teethList = filteredCejTeeth.sort().join(', ');
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'cej_not_visible',
      title: 'CEJ not visible',
      description: `CEJ not visible for #${teethList}.`,
      teethList,
      severity: 'warning',
      completed: false,
    });
  }

  return findings;
}
